import { Button, Space, Typography, Form, InputNumber, Input, Select, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruck, faPlus, faX, faCircleQuestion } from '@fortawesome/pro-solid-svg-icons'
import { TrucksStepProps } from '../../types'
import { MAX_ENTITIES, OWNERSHIP_TYPE_OPTIONS } from '../../constants'
import { checkEquipmentRow } from '../../inputSanityModel'
import InputWarning from '../InputWarning'

const { Title, Paragraph } = Typography

export const TrucksStep = ({
  data,
  onCountChange,
  onTruckUpdate,
  onTruckAdd,
  onTruckRemove, crossInputCard }: TrucksStepProps) => {
  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        width: '100%',
        maxWidth: '1200px',
        padding: '40px 20px'
      }}
    >
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Space direction="horizontal" size="middle" style={{ marginBottom: '16px' }}>
          <FontAwesomeIcon icon={faTruck} style={{ fontSize: '24px', color: '#ff6b4a' }} />
          <Title level={3} style={{ margin: 0 }}>Trucks</Title>
        </Space>

        <Paragraph style={{ color: '#666', marginBottom: '32px' }}>
          Add trucks and calculate depreciation costs.
        </Paragraph>

        {crossInputCard}

        <Form layout="vertical">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form.Item
              label="How many trucks do you have?"
              required
              style={{ marginBottom: 0 }}
              help="Total number of trucks in your fleet"
            >
              <InputNumber
                value={data.truckCount}
                onChange={(value) => onCountChange(value || 0)}
                min={0}
                max={MAX_ENTITIES}
                style={{ width: '100%' }}
                size="large"
              />
            </Form.Item>

            {data.trucks.map((truck, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  padding: '24px',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <Title level={5} style={{ margin: 0 }}>Truck #{index + 1}</Title>
                  <Button
                    type="text"
                    icon={<FontAwesomeIcon icon={faX} />}
                    onClick={() => onTruckRemove(index)}
                    style={{ color: '#999' }}
                  />
                </div>

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'flex-start' }}>
                    <Form.Item
                      label="Truck Name"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input
                        value={truck.name}
                        onChange={(e) => onTruckUpdate(index, 'name', e.target.value)}
                        size="large"
                        placeholder="Truck 1"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Ownership Type"
                      required
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Select
                        value={truck.ownershipType}
                        onChange={(value) => onTruckUpdate(index, 'ownershipType', value)}
                        size="large"
                        options={OWNERSHIP_TYPE_OPTIONS}
                      />
                    </Form.Item>
                  </Space>

                  {truck.ownershipType === 'Own' ? (
                    <>
                      <Space direction="horizontal" size="large" style={{ width: '100%', alignItems: 'flex-start' }}>
                        <Form.Item
                          label={
                            <span>
                              Replacement Cost{' '}
                              <Tooltip title="The cost to replace this truck with a similar vehicle">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            prefix="$"
                            value={truck.replacementCost}
                            onChange={(value) => onTruckUpdate(index, 'replacementCost', value)}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="80,000"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Avg Useful Life{' '}
                              <Tooltip title="Average number of years this truck will be in service">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            value={truck.avgUsefulLife}
                            onChange={(value) => onTruckUpdate(index, 'avgUsefulLife', value)}
                            min={0}
                            addonAfter="Years"
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="7"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Sell Price{' '}
                              <Tooltip title="Expected resale value at the end of its useful life">
                                <FontAwesomeIcon icon={faCircleQuestion} style={{ color: '#999', fontSize: '14px' }} />
                              </Tooltip>
                            </span>
                          }
                          required
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <InputNumber
                            prefix="$"
                            value={truck.sellPrice}
                            onChange={(value) => onTruckUpdate(index, 'sellPrice', value)}
                            min={0}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="25,000"
                          />
                        </Form.Item>
                      </Space>

                      <InputWarning warning={checkEquipmentRow(truck)} />
                    </>
                  ) : (
                    <Form.Item
                      label="Monthly Payment"
                      required
                      style={{ flex: 1, marginBottom: 0, maxWidth: '50%' }}
                    >
                      <InputNumber
                        prefix="$"
                        value={truck.monthlyPayment}
                        onChange={(value) => onTruckUpdate(index, 'monthlyPayment', value)}
                        min={0}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                        style={{ width: '100%' }}
                        size="large"
                        placeholder="1,000"
                      />
                    </Form.Item>
                  )}
                </Space>
              </div>
            ))}

            <Button
              size="large"
              onClick={onTruckAdd}
              icon={<FontAwesomeIcon icon={faPlus} />}
              style={{ width: '100%' }}
              disabled={data.trucks.length >= MAX_ENTITIES}
            >
              Add Another Truck
            </Button>
          </Space>
        </Form>
      </div>
    </Space>
  )
}
